<template>
    <div class="row q-col-gutter-md">
        <div class="col col-12">
            <q-tabs
                v-model="RebitAccountObj.type"
                align="justify"
                class="text-input-color"
                :breakpoint="0"
            >
                <q-tab name="Account" icon="fa fa-university" label="Bank Account" @click="refresh_fields"/>
                <q-tab name="Wallet address" icon="fab fa-btc" label="Crypto Wallet" @click="refresh_fields"/>
                <q-tab name="Cash" icon="fa fa-coins" label="Cash" @click="refresh_fields"/>
            </q-tabs>
        </div> <!-- ACCOUNT TYPE -->
        <div class="col col-12">
            <q-input
                v-model="RebitAccountObj.holder"
                :label="$t('rebit-accounts.table_titles.holder')"
                :error-message="RebitAccountObj.errors.get('holder')"
                lazy-rules
                :rules="[ val => val && val.length > 0 || $t('rebit-accounts.table_titles.holder') + ' is required!']"
                color="input-color"
            />
        </div> <!-- HOLDER'S NAME -->
        <div class="col col-12">
            <q-input
                v-model="RebitAccountObj.description"
                :label="$t('rebit-accounts.table_titles.description')"
                :error-message="RebitAccountObj.errors.get('description')"
                lazy-rules
                :rules="[ val => val && val.length > 0 || $t('rebit-accounts.table_titles.description') + ' is required!']"
                color="input-color"
            />
        </div> <!-- LABEL -->
        <div class="col col-sm-6 col-xs-12">
            <q-select
                v-model="RebitAccountObj.owner_operator_id"
                :options="operators"
                fill-input
                option-value="id"
                option-label="name"
                emit-value
                map-options
                :label="$t('rebit-accounts.table_titles.owner_operator')"
                :error="RebitAccountObj.errors.has('currency_acronym')"
                color="input-color"
                :loading="loading"
            >
                <template v-slot:option="scope">
                    <q-item
                        v-bind="scope.itemProps"
                        v-on="scope.itemEvents"
                        style="border-bottom:1px solid #999"
                    >
                        <q-item-section>
                            <q-item-label lines="1">
                                <span class="text-bold">{{scope.opt.name}}</span>
                            </q-item-label>
                        </q-item-section>
                    </q-item>
                </template>
            </q-select>
        </div> <!-- OWNER OPERATOR -->
        <div class="col col-sm-6 col-xs-12">
            <q-select
                v-model="RebitAccountObj.currency_acronym"
                :options="['Account', 'Cash'].includes(RebitAccountObj.type) ? fiats : all_currencies"
                fill-input
                option-value="acronym"
                option-label="name"
                emit-value
                map-options
                :label="$t('rebit-accounts.table_titles.currency')"
                :error="RebitAccountObj.errors.has('currency_acronym')"
                color="input-color"
                :loading="loading"
                @input="loadCorporations"
            >
                <template v-slot:option="scope">
                    <q-item
                        v-bind="scope.itemProps"
                        v-on="scope.itemEvents"
                        style="border-bottom:1px solid #999"
                    >
                        <q-item-section avatar>
                            <q-icon :name="scope.opt.is_fiat ? 'fa fa-dollar-sign' : 'fab fa-btc'" color="purple"/>
                        </q-item-section>
                        <q-item-section>
                            <q-item-label lines="1">
                                <span class="text-weight-medium text-uppercase">({{scope.opt.acronym}}) - </span>
                                <span class="text-bold">{{scope.opt.name}}</span>
                            </q-item-label>
                        </q-item-section>
                    </q-item>
                </template>
            </q-select>
        </div> <!-- CURRENCY -->
        <div class="col col-12" v-if="RebitAccountObj.type !== 'Cash'">
            <q-select
                :label="bank_label"
                use-input
                v-model="RebitAccountObj.accountable_id"
                :options="RebitAccountObj.corporations"
                option-value="id"
                option-label="name"
                emit-value
                map-options
                lazy-rules
                :error="RebitAccountObj.errors.has('accountable_id')"
                :error-message="RebitAccountObj.errors.get('accountable_id')"
                :rules="[ val => !!val || bank_label + ' is required!']"
                color="input-color"
                :disable="! (RebitAccountObj.currency_acronym || loading_corporations)"
                :loading="loading_corporations"
                v-if="RebitAccountObj.corporations"
            />
        </div> <!-- Bank / Exchange -->
        <div class="col col-xs-12 col-sm-6 col-md-6">
            <q-input
                v-model="RebitAccountObj.opening_balance"
                :label="$t('rebit-accounts.table_titles.opening_balance')"
                :error="RebitAccountObj.errors.has('opening_balance')"
                :error-message="RebitAccountObj.errors.get('opening_balance')"
                color="input-color"
                type="number"
            />
        </div> <!-- OPENING BALANCE -->
        <div class="col col-xs-12 col-sm-6 col-md-6">
            <q-input
                v-model="RebitAccountObj.real_balance"
                :label="$t('rebit-accounts.table_titles.real_balance')"
                :error="RebitAccountObj.errors.has('real_balance')"
                :error-message="RebitAccountObj.errors.get('real_balance')"
                color="input-color"
                type="number"
            />
        </div> <!-- REAL BALANCE -->
        <div class="col col-xs-12 col-sm-6 col-md-6">
            <q-input
                v-model="RebitAccountObj.ideal_balance"
                :label="$t('rebit-accounts.table_titles.ideal_balance')"
                :error="RebitAccountObj.errors.has('ideal_balance')"
                :error-message="RebitAccountObj.errors.get('ideal_balance')"
                color="input-color"
                type="number"
            />
        </div> <!-- IDEAL BALANCE -->
        <div class="col col-xs-12 col-sm-6 col-md-6">
            <q-input
                v-model="RebitAccountObj.monthly_turnover_limit"
                :label="$t('rebit-accounts.table_titles.monthly_turnover_limit')"
                :error="RebitAccountObj.errors.has('monthly_turnover_limit')"
                :error-message="RebitAccountObj.errors.get('monthly_turnover_limit')"
                color="input-color"
                type="number"
            />
        </div> <!-- IDEAL BALANCE -->
        <div class="col col-xs-12 col-sm-6 col-md-3">
            <p class="text-caption q-pb-xs" style="margin:0">Status</p>
            <q-toggle
                v-model="RebitAccountObj.is_active"
                checked-icon="fa fa-check"
                color="green"
                :label="RebitAccountObj.is_active ? 'Active' : 'Inactive'"
                unchecked-icon="fa fa-uncheck"
            />
        </div> <!-- ACTIVE -->
        <div class="col col-xs-12 col-sm-6 col-md-3">
            <p class="text-caption q-pb-xs" style="margin:0">Is depositable?</p>
            <q-toggle
                v-model="RebitAccountObj.is_depositable"
                checked-icon="fa fa-check"
                color="green"
                :label="RebitAccountObj.is_depositable ? 'Yes' : 'No'"
                unchecked-icon="fa fa-uncheck"
            />
        </div> <!-- IS DEPOSITABLE -->
        <div class="col col-12" v-if="RebitAccountObj.type !== 'Cash'">
            <q-input
                v-model="RebitAccountObj.auth_params"
                :label="$t('rebit-accounts.auth_params')"
                outlined
                type="textarea"
                color="input-color"
            />
        </div>
    </div>
</template>

<script>

import Currency from "Modules/Currency/resources/models/Currency"
import Operator from "Modules/Operator/resources/models/Operator"
import Bank from "Modules/Bank/resources/models/Bank"
import {mapActions, mapGetters} from 'vuex'

export default {
  name: 'Form',
  model: {
    prop: 'RebitAccountObj',
    event: 'change'
  },
  props: {
    RebitAccountObj: {
      type: Object,
      required: true
    }
  },
  computed: {
      ...mapGetters(['exchanges']),
      bank_label() {
          const bank_labels = {
              'Account': this.$t('rebit-accounts.table_titles.bank'),
              'Wallet address': this.$t('rebit-accounts.table_titles.exchange')
          }
          return bank_labels[this.RebitAccountObj.type]
      },
      fiats() {
          return this.all_currencies.filter(currency => currency.is_fiat)
      },
      cryptos() {
          return this.all_currencies.filter(currency => !currency.is_fiat)
      }
  },
  data() {
    return {
        loading: false,
        loading_corporations: false,
        all_currencies: [],
        operators: [],
        types: ['Account', 'Wallet address', 'Cash'],
    }
  },
  methods: {
    ...mapActions(['set_exchanges']),
    submit() {
      this.$emit('submit')
    },
    refresh_fields() {
        this.RebitAccountObj.currency_acronym = null
        this.RebitAccountObj.number = null
        this.RebitAccountObj.accountable_id = null
        this.RebitAccountObj.network = null
    },
    loadCurrencies() {
        this.loading = true
        Currency.all(`sort=name%7Casc&per_page=999&filter=`,({data}) => {
            this.all_currencies = data
            this.loading = false
        }).catch(error => {
            this.loading = false
        })
    },
    loadOperators() {
        this.loading = true
        Operator.all(`sort=name%7Casc&per_page=999&filter=`,({data}) => {
            this.operators = data
            this.loading = false
        }).catch(error => {
            this.loading = false
        })
    },
    loadCorporations() {
        if (this.RebitAccountObj.type === 'Account') {
            this.loading_corporations = true
            Bank.list(this.RebitAccountObj.currency_acronym, (data) => {
                this.RebitAccountObj.corporations = data
                this.loading_corporations = false
            }).catch(error => {
                this.loading_corporations = false
            })
        }

        if (this.RebitAccountObj.type === 'Wallet address') {
            this.RebitAccountObj.corporations = this.exchanges
        }
    }
  },
  created() {
      this.loadCurrencies()
      this.loadOperators()
      this.set_exchanges()
  },
  components: {},
}
</script>

<style scoped>

</style>
